<template>
  <div>
    <div
      ref="MobileBookNow"
      class="lg:hidden flex flex-row items-center justify-between fixed w-full bottom-0 left-0 right-0 bg-white border-t border-neutral-300 px-4 py-2 h-20 z-40"
    >
      <div class="flex-1 min-h-12">
        <div v-if="!hasQuote">
          <div class="text-sm text-neutral-700 leading-tight tracking-tight sm:text-lg w-full">
            <fa
              :icon="['fal', 'calendar-day']"
              class="fa-fw text-sm"
            />
            {{ $t('quote.choose-date-for-prices') }}
          </div>
          <div
            v-if="partner.rating_count > 0"
            class="flex items-center justify-start text-base tracking-tight mt-2"
          >
            <fa
              :icon="['fas', 'star']"
              class="fa-fw text-base text-bubblegum-500 mr-1"
            />
            <a href="#reviews">
              <span class="font-bold mr-1 text-neutral-900">
                {{ partner.rating_average }}
              </span>
              <span class="font-light text-neutral-600">
                ({{ partner.rating_count }} {{ $tc('quote.review', partner.rating_count).toLowerCase() }})
              </span>
            </a>
          </div>
        </div>
        <div v-if="hasQuote">
          <div
            v-if="hidePricing"
            class="flex flex-col"
          >
            <span class="text-neutral-900 text-xl font-semibold leading-none">
              {{ $t('quote.free') }}
            </span>
            <span
              v-if="getUser.organization"
              class="text-neutral-500 text-sm leading-none"
            >
              {{ $t('quote.with-your-plan') }}
            </span>
          </div>
          <div
            v-else
            class="text-2xl font-bold text-neutral-900 group-hover:text-neutral-600 group-focus:outline-none"
          >
            <vetster-discount slot-class="text-base font-normal">
              {{
                $n(formatMoney(getQuote.total.amount), {
                  style: 'currency',
                  currency: getQuote.total.currency,
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 2,
                })
              }}
            </vetster-discount>
          </div>
          <div
            v-if="!hidePricing"
            class="font-light text-sm -mt-1"
          >
            {{ $t('quote.including-taxes-and-fees-short') }}
          </div>
        </div>
      </div>
      <div class="flex flex-1 items-center justify-end mt-1 min-h-[42px] min-w-[187px]">
        <vetster-button
          variant="bubblegum"
          size="base"
          class="w-full sm:w-auto min-w-[187px]"
          @click="showTakeover"
        >
          <span v-if="hasQuote">{{ $t('quote.book-now') }}</span>
          <span v-else>{{ $t('quote.check-availability') }}</span>
        </vetster-button>
      </div>
    </div>

    <div
      v-if="shouldShowTakeover"
      class="z-998 fixed w-full h-full bg-black/75 inset-0"
    >
      <button
        :aria-label="$t('app.close')"
        class="absolute top-2 right-4"
        type="button"
        @click="closeTakeover"
      >
        <fa
          :icon="['fal', 'times']"
          class="text-2xl text-white hover:text-neutral-300 transition"
        />
      </button>
    </div>
    <div
      v-if="shouldShowTakeover"
      class="z-999 w-full h-full bg-white fixed inset-0 p-4 rounded-t-lg mt-10 pb-14"
    >
      <transition name="fade">
        <div
          v-if="loading"
          class="w-full h-full bg-white/75 absolute z-40 flex items-center justify-center"
        >
          <fa
            :icon="['fal', 'circle-notch']"
            class="text-5xl text-indigo-400 fa-spin"
          />
        </div>
      </transition>
      <div class="flex flex-col justify-between h-full checkout-window-mobile">
        <vetster-day-picker
          v-if="pageReady"
          :date="getInputs.date"
          :timezone="getInputs.timezone"
          @update="onDayChange"
        />
        <div class="mt-4 bg-neutral-100 p-2 rounded-md">
          <transition
            name="fade"
            mode="out-in"
          >
            <vetster-select
              v-if="shouldShowTimezones"
              v-model="getSetTimezone"
              :options="getTimezoneOptions(getTimezone)"
              :disabled="loading"
              @change="onUpdateTimezone"
            />
            <div
              v-else
              class="flex items-center justify-between text-xs"
            >
              <div class="text-neutral-600">
                {{ $t('app.timezone') }}:
                <span class="font-semibold">
                  {{ humanReadableTimezone(getInputs.timezone) }}
                </span>
              </div>
              <button
                class="text-ocean-900 hover:text-ocean-800 hover:opacity-50 cursor-pointer focus:outline-none focus:underline"
                type="button"
                @click="$store.commit('quote/setShouldShowTimezones', true)"
              >
                {{ $t('app.change') }}
              </button>
            </div>
          </transition>
        </div>
        <div
          ref="appointments"
          class="mt-3 flex-1 overflow-y-auto pr-2"
        >
          <template v-if="hasNoAvailability && !loading">
            <div
              v-if="hasRequestToBookEnabled && hasAppointmentInquiry && !hasPendingAppointmentInquiry && !showAppointmentInquiryConfirmScreen"
              class="mt-8 px-3 overflow-y-hidden w-full flex flex-col flex-1 checkout-appointments"
            >
              <div class="text-center font-bold text-neutral-700 text-xl">
                {{ $t('quote.your-request-is-in-progress') }}
              </div>
              <br />
              <div class="text-left">
                {{ $t('quote.a-request-is-under-way') }}
              </div>
              <br />
              <div class="text-left">
                {{ $t('quote.meanwhile-we-have-thousand-of-vets') }}
              </div>
            </div>
            <div
              v-else-if="hasPendingAppointmentInquiry && hasRequestToBookEnabled"
              class="grid text-center gap-y-4"
            >
              <img
                :src="successLogoPath"
                alt="Vetster"
                class="w-auto h-24 mx-auto mt-8"
              />
              <div class="text-2xl text-neutral-900 leading-7 font-bold">
                {{ $t('quote.good-news') }}
              </div>
              <div>
                {{ $t('quote.vet-has-the-following-times', { name: partnerModel.fullNameWithTitle() }) }}
              </div>
              <div
                v-for="(quote, index) in getAppointmentInquiry.quotes"
                :key="quote.uuid"
                class="w-full bg-white flex items-center hover:bg-indigo-50 hover:border-ocean-600 px-3 py-3 cursor-pointer relative transition rounded"
                :class="[
                  index !== 0 ? 'mt-2' : '',
                  getQuote.uuid === quote.uuid
                    ? 'border-neutral-900 border-2'
                    : 'border-ocean-300 border',
                ]"
                @click="$store.commit('quote/setQuote', quote)"
                @keydown="$store.commit('quote/setQuote', quote)"
              >
                <div class="items-start w-full text-neutral-700 text-left justify-start">
                  <div class="text-base font-normal">
                    {{ getAppointmentDateWithWeekday(quote.start_at) }}
                  </div>
                  <div class="text-xl font-bold">
                    {{ getAppointmentTime(quote.start_at) }}
                  </div>
                </div>
                <div class="flex-1 items-start justify-start text-right text-neutral-900 font-bold text-xl mt-auto">
                  {{
                    $n(formatMoneyRounded(quote.subtotal.amount), {
                      style: 'currency',
                      currency: quote.currency,
                      maximumFractionDigits: 0,
                      minimumFractionDigits: 0,
                    })
                  }}
                </div>
              </div>
              <transition name="fade">
                <div class="mt-3">
                  <vetster-button
                    class="w-full"
                    size="lg"
                    :disabled="loading || !hasQuote"
                    variant="bubblegum"
                    @click="checkout()"
                  >
                    <span>{{ $t('quote.continue-booking') }}</span>
                    <fa
                      v-if="loading"
                      :icon="['fal', 'circle-notch']"
                      class="text-xl text-white-500 ml-2 fa-spin"
                    />
                  </vetster-button>
                </div>
              </transition>
            </div>
            <div
              v-else-if="hasAppointmentInquiry && requestToBookRequested && hasRequestToBookEnabled && showAppointmentInquiryConfirmScreen"
              class="grid gap-y-4"
            >
              <div class="flex justify-center items-center">
                <img
                  :src="getAssetUrl('images/quote-calendar.svg')"
                  :alt="$t('quote.calendar-without-available-times')"
                  class="w-64 h-32 object-contain"
                />
              </div>
              <p class="font-title text-2xl/7 font-bold text-center text-ocean-900">
                {{ $t('quote.thank-you') }}
              </p>
              <div class="px-4 ml-2">
                <div>
                  {{ $t('quote.the-times-you-have-requested', {
                    name: partnerModel.formalName(),
                  }) }}
                </div>
                <br />
                <div>
                  {{ $t('quote.you-should-now-have-an-email') }}
                </div>
                <br />
                <div>
                  {{ $t('quote.if-dr-does-not-reply-within', {
                    name: partnerModel.formalName(),
                  }) }}
                </div>
              </div>
            </div>
            <div
              v-else-if="showAppointmentInquiryConfirmScreen && hasRequestToBookEnabled"
              class="grid gap-y-4 h-full"
            >
              <vetster-request-to-book
                :date="getInputs.date"
                :partner="partnerModel"
                :mode="requestToBookMode"
                :register-guests="registerGuests"
                :success-logo-path="successLogoPath"
                :loading="loading"
                @cancel="showAppointmentInquiryConfirmScreen = false"
                @isLoading="(value) => requestToBookLoading = value"
                @isRequested="(value) => requestToBookRequested = value"
              />
            </div>
            <div
              v-else
              class="w-full h-full flex flex-col flex-1 checkout-appointments"
            >
              <div class="flex justify-center items-center mt-20 h-36">
                <img
                  :src="getAssetUrl('images/quote-calendar.svg')"
                  :alt="$t('quote.calendar-without-available-times')"
                  class="size-36 object-contain"
                />
              </div>

              <div class="px-4 mt-4 text-neutral-700 text-center space-y-4">
                <p class="font-title text-xl/6 font-bold">
                  {{ $t('quote.dr-isnt-available', { name: partnerModel.formalName(), dayOfWeek: getSelectedDate }) }}
                </p>
                <p>
                  {{ $t('quote.please-choose-a-different-date') }}
                </p>
              </div>

              <vetster-button
                variant="bubblegum"
                :href="guidedSearchUrl"
                class="w-full flex justify-center mt-auto"
              >
                {{ $t('quote.find-next-available-vet') }}
              </vetster-button>
              <vetster-button
                v-if="showRequestToBook"
                :disabled="requestToBookLoading || !!hasAppointmentInquiry"
                variant="textLink"
                class="w-full flex justify-center underline text-sm pb-0"
                @click="promptToConfirmAppointmentRequest"
              >
                {{ $t('quote.request-an-appointment-with', { name: partnerModel.formalName() }) }}
              </vetster-button>
            </div>
          </template>
          <div class="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <button
              v-for="(timeslot, index) in getAvailability"
              :key="`${index}`"
              type="button"
              :aria-label="$t('quote.select-timeslot', {
                timeslot: getAppointmentTimeRange(timeslot, getInputs.timezone, true),
              })"
              :data-selected-timeslot="isTimeslotSelected(timeslot) && !$screen('desktop')"
              class="
                focus:outline-none rounded-lg
                flex flex-col items-center justify-center px-2 py-2
                cursor-pointer relative transition duration-150
              "
              :class="isTimeslotSelected(timeslot) ? 'border-2 border-bubblegum-500 selected-timeslot' : 'border border-neutral-700'"
              @click="$store.dispatch('quote/updateTimeslotAndFetchQuote', timeslot)"
            >
              <span class="text-base font-medium">
                {{ getAppointmentTimeRange(timeslot, getInputs.timezone, true) }}
              </span>
              <span
                v-if="!hidePricing"
                class="text-neutral-900 text-base -mt-1"
              >
                <vetster-discount
                  label-class="font-bold"
                  slot-class="text-xs"
                >
                  {{
                    $n(formatMoneyRounded(timeslot.rate.amount), {
                      style: 'currency',
                      currency: timeslot.rate.currency,
                      maximumFractionDigits: 0,
                      minimumFractionDigits: 0,
                    })
                  }}
                </vetster-discount>
              </span>
            </button>
          </div>
        </div>

        <transition
          v-if="hasAvailability"
          name="fade-fast"
          appear
          mode="out-in"
        >
          <div
            v-if="hasQuote"
            key="quote"
            class="flex justify-between px-3 py-2 bg-neutral-50 rounded-md mt-1 h-16 flex items-center justify-center mt-2"
          >
            <div class="flex flex-col">
              <div class="text-xl font-medium">
                {{ $t('app.total') }}
              </div>
              <div class="text-light text-neutral-400 text-sm -mt-1">
                {{ $t('quote.including-taxes-and-fees') }}
              </div>
            </div>
            <div
              v-if="!hidePricing"
              class="flex flex-col group cursor-pointer"
              @click="showQuoteDetails()"
              @keydown="showQuoteDetails()"
            >
              <div class="text-right text-xl font-bold text-neutral-900 group-hover:text-neutral-600 group-focus:outline-none">
                <vetster-discount slot-class="text-base font-normal">
                  {{
                    $n(formatMoney(getQuote.total.amount), {
                      style: 'currency',
                      currency: getQuote.total.currency,
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 2,
                    })
                  }}
                </vetster-discount>
              </div>
              <span class="underline text-sm text-ocean-900 group-hover:text-ocean-600 -mt-1 cursor-pointer group-focus:outline-none focus:underline">
                {{ $t('quote.view-details') }}
              </span>
            </div>
            <div
              v-else
              class="flex flex-col"
            >
              <span class="text-neutral-900 text-xl font-semibold leading-none text-right">
                {{ $t('quote.free') }}
              </span>
              <span
                v-if="getUser.organization"
                class="text-neutral-500 text-sm leading-none"
              >
                {{ $t('quote.with-your-plan') }}
              </span>
            </div>
          </div>
          <div
            v-else
            key="no-quote"
          >
            <div
              v-if="hasError"
              class="text-center text-red-600 font-light p-3 bg-red-50 rounded-md mt-1 h-16 flex items-center justify-center"
            >
              <fa
                class="fa-fw text-xl mr-1"
                :icon="['fal', 'exclamation-circle']"
              />
              <span class="text-sm">{{ getError }}</span>
            </div>
            <div
              v-else
              class="text-center text-neutral-600 font-bold text-lg p-3 bg-neutral-50 rounded-md mt-1 h-16 flex items-center justify-center"
            >
              {{ $t('quote.choose-an-available-time') }}
            </div>
          </div>
        </transition>
        <div>
          <transition name="fade">
            <div
              v-if="hasAvailability"
              class="mt-3"
            >
              <vetster-button
                size="lg"
                :disabled="loading || !hasQuote"
                variant="bubblegum"
                class="w-full"
                @click="checkout()"
              >
                <span>{{ $t('quote.continue-booking') }}</span>
                <fa
                  v-if="loading"
                  :icon="['fal', 'circle-notch']"
                  class="text-xl text-white-500 ml-2 fa-spin"
                />
              </vetster-button>
            </div>
          </transition>
        </div>
      </div>
      <vetster-quote-details-modal
        v-if="hasQuote"
        ref="quoteDetails"
        :quote="getQuote"
        :timezone="getInputs.timezone"
      />
    </div>
  </div>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faCalendarExclamation,
  faCircleNotch,
  faClock,
} from '@fortawesome/pro-light-svg-icons';
import { faBolt as fasBolt } from '@fortawesome/pro-solid-svg-icons';
import { DateTime } from 'luxon';
import { getCurrentInstance } from 'vue';
import { mapActions, mapGetters, mapState } from 'vuex';

import VetsterRequestToBook from '@/brand-components/VetsterRequestToBook';
import VetsterDayPicker from '@/components/VetsterDayPicker';
import VetsterDiscount from '@/components/VetsterDiscount';
import VetsterQuoteDetailsModal from '@/components/VetsterQuoteDetailsModal';
import VetsterSelect from '@/components/VetsterSelect';
import useAppointmentDatetimeFormat from '@/composables/use-appointment-datetime-format';
import useCheckoutHelpers from '@/composables/use-checkout-helpers';
import useSmartTime from '@/composables/use-smart-time';
import useTimezoneHelpers from '@/composables/use-timezone-helpers';
import {
  formatMoney,
  formatMoneyRounded,
} from '@/helpers/format';
import { getAssetUrl } from '@/helpers/url';
import LocalizesRoutes from '@/mixins/LocalizesRoutes';
import SelectsTimeslot from '@/mixins/SelectsTimeslot';
import User from '@/models/User';

library.add({
  fasBolt,
  faCalendarExclamation,
  faCircleNotch,
  faClock,
});

export default {
  name: 'VetsterQuoteMobile',

  components: {
    VetsterDayPicker,
    VetsterDiscount,
    VetsterQuoteDetailsModal,
    VetsterRequestToBook,
    VetsterSelect,
  },

  mixins: [
    LocalizesRoutes,
    SelectsTimeslot,
  ],

  props: {
    defaultTimezone: {
      type: String,
      default: undefined,
    },
    partner: {
      type: Object,
      required: true,
    },
    successLogoPath: {
      type: String,
      required: true,
    },
  },

  setup() {
    const root = getCurrentInstance().proxy.$root;
    const { getBookingUrl } = useCheckoutHelpers(root);
    const { validDateFrom } = useSmartTime();
    const {
      getTimezoneOptions,
      humanReadableTimezone,
    } = useTimezoneHelpers();
    const {
      getAppointmentTimeRange,
      getAppointmentDateWithWeekday,
      getAppointmentTime,
    } = useAppointmentDatetimeFormat();

    return {
      getAppointmentTimeRange,
      getAppointmentDateWithWeekday,
      getAppointmentTime,
      getBookingUrl,
      getTimezoneOptions,
      humanReadableTimezone,
      validDateFrom,
    };
  },

  data() {
    return {
      partnerModel: new User(this.partner),
      requestToBookLoading: false,
      requestToBookRequested: false,
      showAppointmentInquiryConfirmScreen: false,
      shouldShowTakeover: false,
      getAssetUrl,
    };
  },

  computed: {
    ...mapGetters([
      'getUser',
      'hasUser',
      'hidePricing',
    ]),

    ...mapGetters('authModal', ['willAuthPromise']),

    ...mapGetters('quote', [
      'getError',
      'hasError',
    ]),

    ...mapGetters('quote', [
      'canRequestToBook',
      'getAppointmentInquiry',
      'getAvailability',
      'getQuote',
      'getSelectedDate',
      'getTimezone',
      'hasAppointmentInquiry',
      'hasAvailability',
      'hasNoAvailability',
      'hasPendingAppointmentInquiry',
      'hasQuote',
    ]),

    ...mapState('quote', [
      'loading',
      'pageReady',
      'shouldShowTimezones',
    ]),

    getSetTimezone: {
      get() {
        return this.getInputs.timezone;
      },

      set(value) {
        this.$store.commit('quote/setInputsTimezone', value);
      },
    },

    hasRequestToBookEnabled() {
      return this.canRequestToBook
        && this.$statsig.ready
        && this.$statsig.checkGate('booking_request_to_book');
    },

    hasRequestToBookPreferredTimeslotEnabled() {
      return this.canRequestToBook
        && this.$statsig.ready
        && this.$statsig.checkGate('booking_request_to_book_pref_time');
    },

    requestToBookMode() {
      if (this.hasRequestToBookPreferredTimeslotEnabled) {
        return 'timeslot';
      }

      return 'request';
    },

    showPendingAppointmentInquiry() {
      return !this.hasAvailability
        && !this.loading
        && !this.requestToBookRequested
        && !this.hasPendingAppointmentInquiry
        && this.hasRequestToBookEnabled
        && !this.showAppointmentInquiryConfirmScreen;
    },

    showRequestToBook() {
      return this.showPendingAppointmentInquiry && !this.hasAppointmentInquiry;
    },
  },

  watch: {
    showRequestToBook(show) {
      if (show && !this.$screen('desktop')) {
        this.trackRequestToBookLoad();
      }
    },
  },

  beforeDestroy() {
    this.$bus.$off('escape', this.closeTakeover);
  },

  async created() {
    this.$bus.$on('escape', this.closeTakeover);

    const timezone = this.defaultTimezone
      ? this.defaultTimezone
      : DateTime.local().zoneName;

    // This is used to prepend the timezone list with the user defined or machine timezone (not always in the options we have)
    // and needs to be separate from the `this.inputs.timezone` which changes based on selection
    this.$store.commit('quote/setTimezone', timezone);

    this.centerSelectedTimeslot();
    await this.fetchVcprLocation();

    if (!this.$screen('desktop')) {
      this.$store.commit('quote/setPageReady', true);
    }
  },

  methods: {
    ...mapActions('authModal', [
      'setAuthModalMode',
      'setIsBooking',
    ]),

    ...mapActions('quote', ['fetchVcprLocation']),

    showTakeover() {
      this.$bus.$emit('disable-scroll');
      this.shouldShowTakeover = true;
    },

    closeTakeover() {
      this.$bus.$emit('enable-scroll');
      this.shouldShowTakeover = false;
    },

    formatMoney,
    formatMoneyRounded,

    /**
     * Opens the quote modal.
     *
     * @return {void}.
     */
    async showQuoteDetails() {
      if (await this.$refs.quoteDetails.open()) {
        this.checkout();
      }
    },

    async checkout() {
      if (this.hasQuote && await this.registerGuests()) {
        window.location.href = this.getBookingUrl(this.getQuote);
      }
    },

    async registerGuests() {
      return !!(this.hasUser || await this.registerNewAccount());
    },

    registerNewAccount() {
      this.setAuthModalMode('register');
      this.setIsBooking(true);

      return this.willAuthPromise;
    },

    /**
     * Fires when a day picker day is selected.
     *
     * @return {void}.
     */
    async onDayChange(date) {
      await this.$store.dispatch('quote/setDateAndFetchAvailability', date);
    },

    /**
     * Fires when the user changes the timezone.
     *
     * @return {void}.
     */
    async onUpdateTimezone() {
      this.scrollTimeslotsToTop();

      await this.$store.dispatch('quote/updateTimezoneAndFetchAvailability');
    },

    scrollTimeslotsToTop() {
      this.$refs.appointments.scrollTop = 0;
    },

    centerSelectedTimeslot() {
      const el = document.querySelector('.selected-timeslot');
      const apt = document.querySelector('.checkout-appointments');

      if (el && apt) {
        apt.scrollTop = el.offsetTop - apt.clientHeight - el.offsetHeight / 2 - 15;
      }
    },

    async promptToConfirmAppointmentRequest() {
      if (!(await this.registerGuests())) {
        return;
      }

      this.showAppointmentInquiryConfirmScreen = true;

      this.$analytics.track({
        eventName: 'rtb_request_click',
        userId: this.$store.state.user?.uuid || null,
        parent: 'partner-page',
        child: 'select-booking',
      });
    },

    trackRequestToBookLoad() {
      this.$analytics.track({
        eventName: 'rtb_request_load',
        userId: this.$store.state.user?.uuid || null,
        parent: 'partner-page',
        child: 'select-booking',
      });
    },
  },
};
</script>

<style scoped>
.checkout-window-mobile {
  min-height: 400px;
}

.checkout-appointments {
  min-height: 150px;
}
</style>
